import React from 'react'
import styled from 'styled-components' 
import { formatDatePretty } from 'funcs/dates';
import { useEffect}  from 'react' 
import { useSelector, useDispatch } from 'react-redux'
import { BiUpvote } from "react-icons/bi";
import SpinnerSmall from 'components/SpinnerSmall';
import { updateQuestionStatus, resetUpvote, upvoteQuestion, setUpvoteQuestionId } from 'features/qaSessions/qaSessionSlice'
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom'
import { pBtnSmall } from 'components/main.styled';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import { useTranslation } from 'react-i18next';

export default function QuestionItem({ question }) {
    const { t } = useTranslation('qna')

    const { sessionId } = useParams() 
    const dispatch = useDispatch()
    const { user } = useSelector((state) => state.auth)
    const { upvoteStatus, sessionData } =  useSelector((state) => state.qASession)   

    const handleUpdateStatus = (newStatus) => {
        dispatch(updateQuestionStatus({
            churchId: sessionData.church._id,
            sessionId: sessionId,
            questionId: question._id,
            newStatus: newStatus
        }))
    }

    const handleUpvote = () => { 
        if(question.upvotes.includes(user._id)){ 
            return toast.error(t('alreadyUpvoted'))
        }else{
            toast.success(t('upvoted'))
        }
        
        dispatch(setUpvoteQuestionId(question._id))
        
        dispatch(upvoteQuestion({
            sessionId: sessionId,
            questionId: question._id
        })) 
    }

    useEffect(() => {  
        dispatch(resetUpvote())
    }, [upvoteStatus])

    return (
        <QuestionContainer>
            <QuestionTop>
                {
                    question.isAnonymous ? 
                    (
                        <UserDiv>
                            <NoAccountsIcon size={26} />
                            <span>Anonymous</span>
                        </UserDiv>
                        
                    )   
                    :
                    (
                        <UserDiv>
                            <img
                                src={question.creator.profileImg.url}
                                alt={"creator"}
                                />  
                            <span>{question.creator.fullName}</span>                      
                        </UserDiv>
                    )
                }

                <TimeStamp>
                    {
                        question.status === "approved" ? 
                        (
                            formatDatePretty(question.approvedAt)
                        )
                        :
                        ( 
                            formatDatePretty(question.createdAt)
                        )
                    } 
                </TimeStamp>

            </QuestionTop>
            <QuestionText> { question.text } </QuestionText>

            {
                question.status === "pending" ? 
                (
                    <UpdateStatusBtnWrap>
                        <UpdateStatusBtn
                            onClick={() => handleUpdateStatus("rejected")}
                            >
                            <span>Reject</span>
                        </UpdateStatusBtn>
                        <UpdateStatusBtn
                            approve={true}
                            onClick={() => handleUpdateStatus("approved")}
                            >
                            <span>Approve</span>
                        </UpdateStatusBtn>
                    </UpdateStatusBtnWrap>
                )
                : question.status === "approved" && 
                (
                    <UpdateStatusBtnWrap>
                        {
                            (user.isOrganization || user.isAdmin) && 
                            (
                                <UpdateStatusBtn
                                    style={{gap: "10px", width: "130px"}}
                                    approve={true}
                                    disabled={upvoteStatus === "LOADING"}
                                    onClick={() => handleUpdateStatus("answered")}
                                    >
                                        {
                                            upvoteStatus === "LOADING" ? 
                                            (
                                                <SpinnerSmall />
                                            )
                                            :
                                            ( 
                                                <span>{t('questions.markAnswered')}</span>
                                            )
                                        }

                                </UpdateStatusBtn> 
                            )
                        }
                        { !user.isOrganization &&
                            (
                                <UpdateStatusBtn
                                    style={{gap: "10px", width: "110px"}}
                                    approve={true}
                                    disabled={upvoteStatus === "LOADING"}
                                    onClick={handleUpvote}
                                    >
                                        {
                                            upvoteStatus === "LOADING" ? 
                                            (
                                                <SpinnerSmall />
                                            )
                                            :
                                            (
                                                <>
                                                    <BiUpvote size={16} />
                                                    <span>Upvote</span>
                                                </>
                                            )
                                        }

                                </UpdateStatusBtn> 
                            ) 
                        } 
                    </UpdateStatusBtnWrap>
                )
            } 
        </QuestionContainer>
    )
}

const UpdateStatusBtnWrap = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 4px;
`

const TimeStamp = styled.div` 
    color: #7b7f82;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
`

const UserDiv = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    font-size: 14px;
    color: var(--lightThemeFontColor);
    font-weight: 600;
`

const UpdateStatusBtn = styled(pBtnSmall)` 
    align-self: flex-end;
    justify-self: flex-end; 
    font-size: 12px;
    color: ${props => props.approve ? "#168236" : "#9c3a31"};
    background-color: ${props => props.disabled ? "#f1f1f1": props.approve ? "#eef9f1" : "#f7ebe9"};
    border: 1px solid ${props => props.approve ? "#cdebd5" : "#f1d4d0"};
    border-radius: 7px;
    width: 78px;
    font-weight: 700;
    transition: all 0.25s;
    &:hover{
        color: white;
        background-color: ${props => props.approve ? "#21ad46": "#b73525"};
        border-color: ${props => props.approve ? "#21ad46": "#b73525"};
        transform: translateY(-1px);
    }
`

const QuestionText = styled.p`
    color: var(--lightThemeFontColor);
    font-size: 16px;
    text-align: left;
    line-height: 1.5;
    margin: 6px 0 8px;
`
const QuestionTop = styled.div`
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    img{
        width: 34px;
        height: 34px;
        border-radius: 50%;
        border: 2px solid #eef1f3;
    }
`
const QuestionContainer = styled.div`
    background-color: white;
    border-radius: 8px;
    width: 100%;
    padding: 16px;
    gap: 10px;
    color: var(--lightThemeFontColor);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    border: 1px solid #edf0f2;
    box-shadow: 0 4px 16px rgba(61, 70, 78, 0.10);
    transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease;

    &:hover{
        border-color: #e0e7eb;
        box-shadow: 0 10px 24px rgba(61, 70, 78, 0.14);
        transform: translateY(-1px);
    }
`
